/**
 * useShellEvents — centralized window-event → React state bridge.
 *
 * Migration pattern: as we remove Angular/parcel event dependencies,
 * add new event handlers here. AppShell calls this hook once.
 *
 * Events handled so far:
 *   openProviderDialog  → opens ChangeProviderPopup modal
 *
 * Events dispatched back:
 *   ExitProviderView    → notifies HamMenu parcel that the dialog closed
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface ProviderDialogDetail {
  route: string;
  viewType: 'General' | 'GeoSegmentation' | string;
}

export interface ShellEventsState {
  /** True while the ChangeProviderPopup modal is open */
  providerDialogOpen: boolean;
  providerDialogDetail: ProviderDialogDetail | null;
  /** Call this when the modal closes. providerSelected=true if user picked a provider */
  closeProviderDialog: (providerSelected?: boolean) => void;
}

export function useShellEvents(): ShellEventsState {
  const [providerDialogDetail, setProviderDialogDetail] = useState<ProviderDialogDetail | null>(
    null
  );

  // Keep a ref so the stable closeProviderDialog callback can always read the latest detail
  const providerDialogDetailRef = useRef<ProviderDialogDetail | null>(null);

  // ── openProviderDialog ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleOpen = (e: Event) => {
      const detail = (e as CustomEvent<ProviderDialogDetail>).detail;
      const resolved =
        detail?.route === '/PSMS'
          ? { route: '/PSMS', viewType: 'General' }
          : { route: '/', viewType: 'General' };
      providerDialogDetailRef.current = resolved;
      setProviderDialogDetail(resolved);
    };

    window.addEventListener('openProviderDialog', handleOpen);
    return () => window.removeEventListener('openProviderDialog', handleOpen);
  }, []);

  // ── Close handler — dispatches ExitProviderView back to HamMenu parcel ────
  const closeProviderDialog = useCallback((providerSelected = false) => {
    const detail = providerDialogDetailRef.current;
    providerDialogDetailRef.current = null;
    setProviderDialogDetail(null);

    // Map viewType back to the Angular selectedView key so NavigationBar's
    // ExitProviderView handler can update the dropdown correctly.
    // Angular hamburger-menu afterClosed() shape: { selectedView, providerSelected }
    const viewTypeToAngularView: Record<string, string> = {
      General: 'General',
      GeoSegmentation: 'GeoSegmentation',
    };
    const angularSelectedView = viewTypeToAngularView[detail?.viewType ?? ''] ?? 'General';

    window.dispatchEvent(
      new CustomEvent('ExitProviderView', {
        detail: {
          selectedView: angularSelectedView,
          providerSelected,
        },
      })
    );
  }, []);

  return {
    providerDialogOpen: providerDialogDetail !== null,
    providerDialogDetail,
    closeProviderDialog,
  };
}
